import {
  SESSION_STATE,
  END_REASON,
  IDLE_LIMIT_MS,
  IDLE_WARN_MARGIN_MS,
  RENEW_MARGIN_MS,
  MAX_RENEWAL_ATTEMPTS,
  RETRY_BASE_DELAY_MS,
  RETRY_MAX_DELAY_MS,
  ACTIVITY_THROTTLE_MS,
  MIN_TIMER_DELAY_MS,
  STORAGE_KEYS,
  ACTIVITY_EVENTS,
} from './constants';
import { getTokenExpiry } from './token';

/**
 * The single authority on session lifecycle for a tab.
 *
 * Before this, at least nine places decided independently that the session had
 * ended — the Apollo error link, the 404 component, and seven route guards —
 * each with its own criteria, its own destination, and no idea the others
 * existed. An expiry with six queries in flight produced six redirects and six
 * reloads. This class replaces all of that with one state machine, one timer
 * and one exit.
 *
 * Design rules, each one earning its keep:
 *
 *   - **One timer.** A single setTimeout, cancelled before every reschedule.
 *     Intervals are what accumulate over a long session.
 *   - **The clock outranks the timer.** setTimeout is throttled in background
 *     tabs and does not advance while the machine is asleep, so a tab left open
 *     overnight wakes believing it has hours left. Every wake-up re-reads the
 *     clock instead of trusting the timer that fired it.
 *   - **One renewal in flight.** Concurrent callers share a promise rather than
 *     starting a second request.
 *   - **Epoch guard.** Every renewal captures a generation number; anything that
 *     ends or restarts the session bumps it. A response that lands late finds a
 *     stale epoch and is discarded, so it cannot revive a closed session.
 *   - **Injected time.** now/setTimeout/clearTimeout come from the constructor,
 *     so tests drive suspension and clock jumps without waiting in real time.
 */
export class SessionManager {
  constructor({
    now = () => Date.now(),
    setTimeout: setTimeoutFn = (...args) => window.setTimeout(...args),
    clearTimeout: clearTimeoutFn = (...args) => window.clearTimeout(...args),
    storage = window.localStorage,
    getToken,
    setToken,
    renew,
    onEnd,
    logger = null,
    random = Math.random,
  } = {}) {
    this._now = now;
    this._setTimeout = setTimeoutFn;
    this._clearTimeout = clearTimeoutFn;
    this._storage = storage;
    this._getToken = getToken;
    this._setToken = setToken;
    this._renew = renew;
    this._onEnd = onEnd;
    this._logger = logger;
    this._random = random;

    this._state = SESSION_STATE.ACTIVE;
    // Why we are in that state. EXPIRING means two different things — the token
    // is running out, or the user has gone quiet — and the dialog says something
    // different for each.
    this._stateReason = null;
    this._epoch = 0;
    this._timerId = null;
    this._inflight = null;
    this._attempt = 0;
    this._listeners = new Set();
    this._teardown = [];
    this._lastActivityWrite = 0;
    this._started = false;
    // False once the server has refused for good (the absolute ceiling). The
    // session stays usable until its token expires, but nothing will extend it.
    this._canExtend = true;
  }

  // ---------------------------------------------------------------- lifecycle

  /** Begins supervising the session. Idempotent. */
  start() {
    if (this._started) return;
    this._started = true;

    this._markActivity(true);
    this._bindActivityEvents();
    this._bindVisibility();
    this._bindCrossTab();
    this._schedule();
  }

  /**
   * Starts supervising, or re-derives the schedule if already running.
   *
   * Needed because a sign-in that happens without a page reload gives the tab a
   * token the manager has never seen: it was started with no token to schedule
   * against, and nothing would wake it up again.
   */
  resume() {
    if (!this._started) {
      this.start();
      return;
    }
    if (this._isTerminal()) return;
    // Already supervising: leave the pending timer alone. Callers fire this from
    // render paths, and rescheduling on every render would keep pushing the
    // deadline forward so it never arrives.
    if (this._timerId !== null) return;
    this._schedule();
  }

  /**
   * Releases every timer and listener. Safe to call twice, and safe to call on a
   * session that never started — React can unmount before the first effect runs.
   */
  stop() {
    this._clearTimer();
    this._teardown.forEach((off) => off());
    this._teardown = [];
    this._started = false;
    // Anything still in flight belongs to a manager that no longer exists.
    this._epoch += 1;
    this._inflight = null;
  }

  // -------------------------------------------------------------- observation

  getState() {
    return this._state;
  }

  /** Why the session is in its current state, or null when it needs no reason. */
  getReason() {
    return this._stateReason;
  }

  /** Whether asking for more time can still succeed. */
  canExtend() {
    return this._canExtend;
  }

  /** @returns {() => void} unsubscribe. */
  subscribe(listener) {
    this._listeners.add(listener);
    return () => this._listeners.delete(listener);
  }

  /** Milliseconds until the token expires; negative once it has. */
  timeToExpiry() {
    const expiry = getTokenExpiry(this._getToken());
    return expiry === null ? null : expiry - this._now();
  }

  /** Milliseconds until inactivity ends the session; negative once it has. */
  timeToIdleClose() {
    return IDLE_LIMIT_MS - this.idleFor();
  }

  /**
   * "I am still here" — the idle warning's one button.
   *
   * Records activity and re-derives the schedule on the spot, so the dialog
   * closes on the click rather than whenever the next timer happens to fire.
   * Deliberately no network call: presence is a local fact, and asking the
   * server to confirm it is how activity turns back into a request storm.
   */
  stayActive() {
    if (this._isTerminal()) return;
    this._markActivity(true);
    this._evaluate();
  }

  /** Milliseconds since the user last did anything, across all tabs. */
  idleFor() {
    const raw = Number(this._safeRead(STORAGE_KEYS.LAST_ACTIVITY));
    if (!raw) return 0;
    // A clock moved backwards yields a negative age. Treat it as "just active"
    // rather than letting it read as a fresh session forever.
    return Math.max(0, this._now() - raw);
  }

  // ------------------------------------------------------------------ ending

  /**
   * Ends the session exactly once. Every later call is a no-op, which is what
   * lets N failing queries produce one redirect instead of N.
   */
  end(reason) {
    if (this._isTerminal()) return;

    const state = reason === END_REASON.LOGOUT ? SESSION_STATE.CLOSED : SESSION_STATE.EXPIRED;
    this._epoch += 1;
    this._inflight = null;
    this._clearTimer();
    this._setState(state, reason);
    this._log('session_ended', { reason });
    this._announceEnd();

    if (typeof this._onEnd === 'function') this._onEnd(reason);
  }

  _isTerminal() {
    return this._state === SESSION_STATE.EXPIRED || this._state === SESSION_STATE.CLOSED;
  }

  // ---------------------------------------------------------------- renewal

  /**
   * Renews the token, at most one request at a time.
   *
   * @returns {Promise<boolean>} whether the session is still alive afterwards.
   */
  renewNow() {
    if (this._isTerminal() || !this._canExtend) return Promise.resolve(false);
    // A caller arriving mid-renewal gets the running attempt, not a new one.
    if (this._inflight) return this._inflight;

    this._attempt = 0;
    this._setState(SESSION_STATE.RENEWING);
    this._inflight = this._attemptRenewal(this._epoch).finally(() => {
      this._inflight = null;
    });
    return this._inflight;
  }

  async _attemptRenewal(epoch) {
    while (this._attempt < MAX_RENEWAL_ATTEMPTS) {
      this._attempt += 1;

      let result;
      try {
        result = await this._renew();
      } catch (error) {
        // Network-level failure: recoverable, so back off and try again.
        if (this._isStale(epoch)) return false;
        this._log('renewal_error', { attempt: this._attempt });
        if (this._attempt >= MAX_RENEWAL_ATTEMPTS) {
          this._giveUp();
          return false;
        }
        await this._backoff(this._attempt);
        continue;
      }

      // The session was ended (here or in another tab) while this request was
      // travelling. Applying the token now would resurrect it.
      if (this._isStale(epoch)) {
        this._log('renewal_discarded', { attempt: this._attempt });
        return false;
      }

      if (result && result.token) {
        this._setToken(result.token);
        this._attempt = 0;
        this._log('renewal_ok', {});
        // Not a plain "back to ACTIVE": a renewal proves the token is fresh, not
        // that the user came back, so an idle warning it interrupted has to
        // survive it.
        this._settle();
        return true;
      }

      // A reason without a token is the server saying "do not come back", so no
      // retry budget applies.
      const reason = (result && result.reason) || END_REASON.RENEWAL_FAILED;

      // Hitting the absolute ceiling means no FUTURE token — it does not revoke
      // the one in hand, which is still valid for a few more minutes. Ending the
      // session here would take away exactly the window the user needs to save
      // an evaluation they have been working on for hours. Instead, stop trying,
      // say so, and let the token run out on its own.
      if (reason === END_REASON.ABSOLUTE_LIMIT) {
        const remaining = this.timeToExpiry();
        if (remaining !== null && remaining > 0) {
          this._canExtend = false;
          this._setState(SESSION_STATE.EXPIRING, reason);
          this._log('renewal_refused_final', { reason });
          this._schedule();
          return false;
        }
      }

      this.end(reason);
      return false;
    }

    this._giveUp();
    return false;
  }

  /**
   * The retry budget is gone — but the token in hand may still be good.
   *
   * Ending here would sign out a user whose only problem is a network that
   * dropped for a few seconds, while the credential they are holding is still
   * valid and the server would still serve it. So we say so, offer the retry,
   * and let the token's own expiry end the session if nothing improves.
   */
  _giveUp() {
    const remaining = this.timeToExpiry();
    if (remaining !== null && remaining > 0) {
      this._setState(SESSION_STATE.EXPIRING, END_REASON.RENEWAL_FAILED);
      this._log('renewal_gave_up', {});
      this._schedule();
      return;
    }
    this.end(END_REASON.RENEWAL_FAILED);
  }

  /**
   * Returns to whichever non-terminal state the clock justifies, and reschedules.
   */
  _settle() {
    if (this._isTerminal()) return;

    if (this.timeToIdleClose() <= IDLE_WARN_MARGIN_MS) {
      this._setState(SESSION_STATE.EXPIRING, END_REASON.IDLE);
    } else {
      this._setState(SESSION_STATE.ACTIVE);
    }
    this._schedule();
  }

  /** Exponential backoff with jitter, so retries from many tabs do not align. */
  _backoff(attempt) {
    const capped = Math.min(RETRY_BASE_DELAY_MS * 2 ** (attempt - 1), RETRY_MAX_DELAY_MS);
    const delay = capped / 2 + this._random() * (capped / 2);
    return new Promise((resolve) => this._setTimeout(resolve, delay));
  }

  _isStale(epoch) {
    return epoch !== this._epoch || this._isTerminal();
  }

  // ---------------------------------------------------------------- scheduling

  /**
   * Points the single timer at whichever deadline comes first.
   *
   * The timer only wakes us up; it never decides anything. _evaluate re-reads
   * the clock, which is what makes suspension and background throttling
   * harmless.
   */
  _schedule() {
    this._clearTimer();
    if (this._isTerminal()) return;

    const toExpiry = this.timeToExpiry();
    if (toExpiry === null) return;

    const untilRenew = toExpiry - RENEW_MARGIN_MS;
    const untilIdle = this.timeToIdleClose();
    const untilIdleWarn = untilIdle - IDLE_WARN_MARGIN_MS;

    const next = [untilRenew, untilIdle, untilIdleWarn, toExpiry].filter((ms) => ms > 0);
    const delay = next.length ? Math.min(...next) : 0;

    this._timerId = this._setTimeout(
      () => this._evaluate(),
      Math.max(MIN_TIMER_DELAY_MS, delay)
    );
  }

  /**
   * Decides what the session should do right now, from the clock alone.
   *
   * Order matters: dead sessions are ended without a renewal attempt. Waking a
   * suspended tab and firing a renewal at an already-expired token is precisely
   * how the retry loop this ticket describes gets recreated.
   */
  _evaluate() {
    if (this._isTerminal()) return;

    // Nobody signed in: there is no session to end, so end nothing. This runs on
    // the login screen too — App starts the manager unconditionally — and ending
    // here would bounce a visitor who is merely logged out to the login they are
    // already on, carrying an "your session expired" message about a session
    // that never existed. Idleness is checked after this for the same reason.
    if (!this._getToken()) return;

    const idleRemaining = this.timeToIdleClose();
    if (idleRemaining <= 0) {
      this.end(END_REASON.IDLE);
      return;
    }

    const toExpiry = this.timeToExpiry();
    // A token that decodes but carries no `exp` predates the claim and the server
    // still honours it — hasUsableSession() lets it through the route guard, so
    // killing it here would sign those people out for no reason. Unknown expiry
    // is not an expired one.
    if (toExpiry === null) return;
    if (toExpiry <= 0) {
      this.end(END_REASON.UNAUTHENTICATED);
      return;
    }

    // A failed renewal and the absolute ceiling are conditions, not moments:
    // they hold until something resolves them, and idleness must not overwrite
    // them — both the dialog the user is reading and the "do not retry on a
    // timer" rule below are keyed on that reason.
    const settled =
      this._state === SESSION_STATE.EXPIRING &&
      (this._stateReason === END_REASON.RENEWAL_FAILED ||
        this._stateReason === END_REASON.ABSOLUTE_LIMIT);

    // Decided before the renewal, and deliberately without returning. The two
    // deadlines can land on the same tick — a one-hour token and a one-hour idle
    // limit share the same three-minute margin — and renewing first meant the
    // dialog was skipped on exactly the ticks where it was due.
    if (!settled) {
      if (idleRemaining <= IDLE_WARN_MARGIN_MS) {
        this._setState(SESSION_STATE.EXPIRING, END_REASON.IDLE);
      } else if (
        this._state === SESSION_STATE.EXPIRING &&
        this._stateReason === END_REASON.IDLE
      ) {
        // The user answered, or simply did something: the warning no longer
        // holds. A failed renewal is not undone by moving a mouse.
        this._setState(SESSION_STATE.ACTIVE);
      }
    }

    if (toExpiry <= RENEW_MARGIN_MS) {
      // Asking again after a final refusal would burn the retry budget on an
      // answer that cannot change. Warn instead, and wait out the token.
      if (!this._canExtend) {
        this._setState(SESSION_STATE.EXPIRING, END_REASON.ABSOLUTE_LIMIT);
        this._schedule();
        return;
      }
      // A renewal that already spent its budget is not retried on a timer: the
      // dialog is up with a button, and hammering a server that just failed
      // three times is how a bad minute becomes an outage. The user decides.
      if (this._stateReason === END_REASON.RENEWAL_FAILED) {
        this._schedule();
        return;
      }
      this.renewNow();
      return;
    }

    this._schedule();
  }

  _clearTimer() {
    if (this._timerId !== null) {
      this._clearTimeout(this._timerId);
      this._timerId = null;
    }
  }

  // ------------------------------------------------------------------ activity

  /**
   * Records presence. Throttled hard, and it only ever writes a timestamp —
   * activity must never trigger network calls of its own.
   */
  _markActivity(force = false) {
    const now = this._now();
    if (!force && now - this._lastActivityWrite < ACTIVITY_THROTTLE_MS) return;
    this._lastActivityWrite = now;
    this._safeWrite(STORAGE_KEYS.LAST_ACTIVITY, String(now));
  }

  /**
   * Long-running work — a video upload, playback — counts as the user being
   * present.
   *
   * Throttled like every other activity signal, and that is load-bearing here:
   * callers fire it from `timeupdate` and from upload progress, which run
   * several times a second. Forcing the write would mean thousands of
   * localStorage writes per evaluation.
   */
  reportActivity() {
    if (this._isTerminal()) return;
    this._markActivity();
  }

  _bindActivityEvents() {
    const handler = () => this._markActivity();
    ACTIVITY_EVENTS.forEach((event) => {
      window.addEventListener(event, handler, { passive: true });
      this._teardown.push(() => window.removeEventListener(event, handler));
    });
  }

  _bindVisibility() {
    const handler = () => {
      if (document.visibilityState !== 'visible') return;
      // Re-derive everything from the clock. The timer that should have fired
      // while the tab slept may never have run.
      this._evaluate();
    };
    document.addEventListener('visibilitychange', handler);
    this._teardown.push(() => document.removeEventListener('visibilitychange', handler));
  }

  _bindCrossTab() {
    const handler = (event) => {
      if (event.key !== STORAGE_KEYS.ENDED_AT || !event.newValue) return;
      // Another tab ended the session. Follow it without asking the server —
      // one sign-out must not become one request per open tab.
      this.end(END_REASON.LOGOUT);
    };
    window.addEventListener('storage', handler);
    this._teardown.push(() => window.removeEventListener('storage', handler));
  }

  _announceEnd() {
    this._safeWrite(STORAGE_KEYS.ENDED_AT, String(this._now()));
  }

  // -------------------------------------------------------------------- misc

  _setState(state, reason) {
    const nextReason = reason || null;
    // The reason is part of the state: EXPIRING-because-idle and
    // EXPIRING-because-the-token-is-ending render differently, so a change of
    // reason alone still has to reach the listeners.
    if (this._state === state && this._stateReason === nextReason) return;
    this._state = state;
    this._stateReason = nextReason;
    this._listeners.forEach((listener) => listener(state, nextReason));
  }

  /** Session telemetry never includes the token or anything derived from it. */
  _log(event, data) {
    if (!this._logger) return;
    this._logger(event, {
      ...data,
      state: this._state,
      idle_minutes: Math.round(this.idleFor() / 60000),
    });
  }

  _safeRead(key) {
    try {
      return this._storage.getItem(key);
    } catch {
      return null;
    }
  }

  _safeWrite(key, value) {
    try {
      this._storage.setItem(key, value);
    } catch {
      // Storage can be full or blocked (Safari private mode). Losing the
      // timestamp must never break the session.
    }
  }
}

export default SessionManager;
